<template>
  <div
    class="app-stepper"
    :class="{ 'app-stepper--grow': grow, 'app-stepper--disabled': disabled }"
    role="group"
    :aria-label="groupLabel"
  >
    <app-icon-button
      class="app-ib--compound app-stepper__arrow app-stepper__prev"
      icon="chevL"
      label="Vorheriger Monat"
      :boxed="false"
      :disabled="prevDisabled"
      @click="step(-1)"
    />
    <v-menu
      ref="menu"
      v-model="menuOpen"
      offset-y
      nudge-bottom="4"
      min-width="auto"
      :disabled="disabled"
      :close-on-content-click="false"
      content-class="app-menu app-menu--picker"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="app-stepper__mid"
          :disabled="disabled"
          :aria-label="'Monat wählen, ' + monthText"
          v-bind="attrs"
          v-on="on"
        >
          <app-icon name="calendar" :size="16" class="app-stepper__cal" />
          <span class="app-stepper__text">{{ midText }}</span>
        </button>
      </template>
      <v-date-picker
        :value="validValue || undefined"
        type="month"
        no-title
        locale="de"
        color="primary"
        :min="min || undefined"
        :max="max || undefined"
        @input="pick"
      ></v-date-picker>
    </v-menu>
    <app-icon-button
      class="app-ib--compound app-stepper__arrow app-stepper__next"
      icon="chevR"
      label="Nächster Monat"
      :boxed="false"
      :disabled="nextDisabled"
      @click="step(1)"
    />
  </div>
</template>

<script>
// Monatsschritt (Spezifikation 2.6): Verbund ‹ · „Oktober 2026“ · ›; die Mitte öffnet einen Monats-Datumspicker.
// value/min/max im Format 'YYYY-MM' wie alle Monatsfelder der App; max/min null = unbegrenzt.
// prefix zeigt „Von: Oktober 2026“. Ereignis input ('YYYY-MM'). Pfeil-Tooltips nur bei Maus.
import moment from 'moment';
import AppIcon from './AppIcon.vue';
import AppIconButton from './AppIconButton.vue';
import { raiseMenu } from './AppSelect.vue';
import { formatMonth } from '../../utils/format';

const MONAT = /^\d{4}-\d{2}$/;

export default {
  name: 'AppMonthStepper',
  components: { AppIcon, AppIconButton },
  props: {
    value: { type: String, default: null },
    min: { type: String, default: null },
    max: { type: String, default: null },
    grow: { type: Boolean, default: false },
    prefix: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    // aria-label der Gruppe
    label: { type: String, default: null }
  },
  data() {
    return { menuOpen: false };
  },
  computed: {
    validValue() {
      return this.value && MONAT.test(this.value) ? this.value : null;
    },
    monthText() {
      return this.validValue ? formatMonth(this.validValue) : 'Monat wählen';
    },
    midText() {
      return this.prefix ? this.prefix + ': ' + this.monthText : this.monthText;
    },
    groupLabel() {
      return this.label || this.prefix || 'Berichtsmonat';
    },
    prevDisabled() {
      return this.disabled || !this.validValue || (!!this.min && this.validValue <= this.min);
    },
    nextDisabled() {
      return this.disabled || !this.validValue || (!!this.max && this.validValue >= this.max);
    }
  },
  mounted() {
    raiseMenu(this.$refs.menu);
  },
  methods: {
    step(delta) {
      if (!this.validValue) {
        return;
      }
      let next = moment(this.validValue, 'YYYY-MM').add(delta, 'months').format('YYYY-MM');
      if (this.max && next > this.max) {
        next = this.max;
      }
      if (this.min && next < this.min) {
        next = this.min;
      }
      if (next !== this.value) {
        this.$emit('input', next);
      }
    },
    pick(month) {
      this.menuOpen = false;
      if (month && month !== this.value) {
        this.$emit('input', month);
      }
    }
  }
};
</script>
